import { api } from '@/api/instance';
import { getUserStoreState } from '@/store';
import { getErrorMessage, isAxiosError } from '../axios.config';
import { clearPreAuthCookies } from '../rubiauth.instance';

export interface UserInfo {
  id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  color?: string | number;
  emailNotification?: boolean;
  subscription?: boolean;
  [key: string]: unknown;
}

export interface UpdateUserInfoPayload {
  first_name: string;
  last_name: string;
  email?: string;
  color?: string | number;
  emailNotification?: boolean;
  subscription?: boolean;
}

export interface UpdateAvatarColorPayload {
  avatarColorId: string | number;
}

export const USER_INFO_QUERY_KEY = ['userInfo'] as const;

function buildFallbackUserInfo(): UserInfo {
  const { currentUser, settings } = getUserStoreState();
  if (currentUser) {
    return {
      first_name: currentUser.user.firstName,
      last_name: currentUser.user.lastName,
      email: currentUser.user.email,
      color: settings.color ?? '',
    };
  }
  return { first_name: '', last_name: '', email: '', color: settings.color ?? '' };
}

/**
 * Fetches the current user's profile by email, sending the session cookie.
 *
 * The user-service endpoint expects the email in the URL path; the cookie
 * identifies the session. We clear transitional cookies first so the
 * backend only sees the real session-auth-access-token.
 */
export const getUserInfo = async (): Promise<UserInfo> => {
  const currentUser = getUserStoreState().currentUser;
  const email = currentUser?.user.email;

  if (!email) {
    throw new Error('No authenticated user email available');
  }

  try {
    const response = await api.get(`/user/${encodeURIComponent(email)}`, {
      withCredentials: true,
    });

    return response.data.result;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      return buildFallbackUserInfo();
    }
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }
};

/**
 * Updates user information
 */
export const updateUserInfo = async (body: UpdateUserInfoPayload) => {
  clearPreAuthCookies();

  try {
    const response = await api.patch('/user/profile', body, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }
};

/**
 * Updates user avatar color
 */
export const updateUserAvatarColor = async (body: UpdateAvatarColorPayload) => {
  clearPreAuthCookies();

  try {
    const response = await api.patch('/user/', body, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }
};

/**
 * Gets available avatar colors
 */
export const getAvatarColors = async () => {
  clearPreAuthCookies();

  try {
    const response = await api.get('/user/', {
      withCredentials: true,
    });
    return response.data.result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }
};
